import { useEffect, useRef, useState, useCallback } from "react"
import confetti from "canvas-confetti"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { apiClient } from "@/lib/api-client"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import {
  ONBOARDING_WELCOME_CONTENT,
  ONBOARDING_WELCOME_SEEN,
  type WelcomeContent,
} from "@/modules/employee/employee/dto/welcome"
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
} from "lucide-react"

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

function VideoPlayer({ src }: { src: string }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)

  const resetHideTimer = useCallback(() => {
    setShowControls(true)
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current)
    hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000)
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    const onTimeUpdate = () => setCurrentTime(video.currentTime)
    const onLoadedMetadata = () => setDuration(video.duration)
    const onFullscreenChange = () =>
      setFullscreen(!!document.fullscreenElement)

    video.addEventListener("play", onPlay)
    video.addEventListener("pause", onPause)
    video.addEventListener("timeupdate", onTimeUpdate)
    video.addEventListener("loadedmetadata", onLoadedMetadata)
    document.addEventListener("fullscreenchange", onFullscreenChange)

    // Autoplay: start muted to satisfy browser policy, then unmute
    video.muted = true
    video.play().then(() => {
      video.muted = false
      setMuted(false)
    }).catch(() => {
      // Autoplay blocked — stay paused
    })

    resetHideTimer()

    return () => {
      video.removeEventListener("play", onPlay)
      video.removeEventListener("pause", onPause)
      video.removeEventListener("timeupdate", onTimeUpdate)
      video.removeEventListener("loadedmetadata", onLoadedMetadata)
      document.removeEventListener("fullscreenchange", onFullscreenChange)
      if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current)
    }
  }, [resetHideTimer])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) video.play()
    else video.pause()
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current
    if (!video) return
    video.currentTime = Number(e.target.value)
  }

  const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value)
    setVolume(v)
    setMuted(v === 0)
    if (videoRef.current) videoRef.current.volume = v
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return
    const next = !muted
    setMuted(next)
    video.muted = next
  }

  const toggleFullscreen = () => {
    const container = containerRef.current
    if (!container) return
    if (!document.fullscreenElement) container.requestFullscreen()
    else document.exitFullscreen()
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-black rounded-xl overflow-hidden group select-none"
      style={{ aspectRatio: "16/9", maxHeight: "55vh" }}
      onMouseMove={resetHideTimer}
      onMouseLeave={() => {
        if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current)
        setShowControls(false)
      }}
      onMouseEnter={resetHideTimer}
    >
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-contain cursor-pointer"
        onClick={togglePlay}
      />

      {/* Centre play/pause flash */}
      <div
        className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 ${showControls && !playing ? "opacity-100" : "opacity-0"}`}
      >
        <div className="bg-black/40 rounded-full p-5">
          <Play className="w-10 h-10 text-white fill-white" />
        </div>
      </div>

      {/* Controls bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 px-4 pb-3 pt-10 transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)",
        }}
      >
        {/* Progress bar */}
        <div className="relative w-full h-1.5 mb-3 group/seek">
          <div className="absolute inset-0 rounded-full bg-white/20" />
          <div
            className="absolute left-0 top-0 h-full rounded-full bg-white transition-all"
            style={{ width: `${progress}%` }}
          />
          <input
            type="range"
            min={0}
            max={duration || 100}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        <div className="flex items-center gap-3">
          {/* Play / Pause */}
          <button
            onClick={togglePlay}
            className="text-white hover:text-white/80 transition-colors"
          >
            {playing ? (
              <Pause className="w-5 h-5 fill-white" />
            ) : (
              <Play className="w-5 h-5 fill-white" />
            )}
          </button>

          {/* Volume */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={toggleMute}
              className="text-white hover:text-white/80 transition-colors"
            >
              {muted || volume === 0 ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={handleVolume}
              className="w-20 accent-white cursor-pointer"
            />
          </div>

          {/* Time */}
          <span className="text-white/80 text-xs tabular-nums flex-1">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className="text-white hover:text-white/80 transition-colors"
          >
            {fullscreen ? (
              <Minimize className="w-5 h-5" />
            ) : (
              <Maximize className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function WelcomeDialog() {
  const [content, setContent] = useState<WelcomeContent | null>(null)
  const [open, setOpen] = useState(false)
  const [dismissing, setDismissing] = useState(false)

  useEffect(() => {
    apiClient
      .get(ONBOARDING_WELCOME_CONTENT)
      .then((res) => {
        if (res.status === HttpStatusCodes.OK && res.data) {
          setContent(res.data)
          setOpen(true)
          confetti({
            particleCount: 150,
            spread: 80,
            origin: { y: 0.4 },
          })
        }
      })
      .catch(() => {
        // No welcome content or already seen — do nothing
      })
  }, [])

  const handleDismiss = async () => {
    setDismissing(true)
    try {
      await apiClient.post(ONBOARDING_WELCOME_SEEN)
    } catch {
      // Best-effort; dialog still closes
    } finally {
      setOpen(false)
      setDismissing(false)
    }
  }

  if (!content) return null

  const hasVideo = !!content.welcomeVideoUrl
  const hasMessage = !!content.welcomeMessage

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleDismiss()}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-3xl w-[90vw] max-h-[90vh] flex flex-col"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-xl">
            Welcome to {content.organizationName}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 min-h-0 flex-1">
          {hasVideo ? (
            <VideoPlayer src={content.welcomeVideoUrl!} />
          ) : hasMessage ? (
            <div className="bg-muted border border-border rounded-lg p-8">
              <p className="text-foreground whitespace-pre-wrap leading-relaxed text-lg">
                {content.welcomeMessage}
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            onClick={handleDismiss}
            disabled={dismissing}
            className="px-8 py-2"
          >
            {dismissing ? "Please wait..." : "Get Started"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
